import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { moderateContent, sanitizeInput } from '../../../utils/contentModeration';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommentSection = ({ blogId, blogSlug }) => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedText, setEditedText] = useState('');

  useEffect(() => {
    if (blogId) {
      fetchComments();
    }
  }, [blogId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      
      // Fetch approved comments for everyone
      let query = supabase
        ?.from('blog_comments')
        ?.select('*')
        ?.eq('blog_id', blogId);

      // If user is logged in, get all their comments (approved, flagged, rejected)
      // Otherwise, only get approved comments
      if (user) {
        query = query?.or(`status.eq.approved,user_id.eq.${user.id}`);
      } else {
        query = query?.eq('status', 'approved');
      }

      const { data, error: fetchError } = await query?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const formattedComments = data?.map(comment => ({
        id: comment?.id,
        userId: comment?.user_id,
        author: comment?.author_name,
        role: comment?.author_role || 'Reader',
        date: comment?.created_at?.split('T')?.[0],
        text: comment?.comment_text,
        status: comment?.status,
        verified: comment?.status === 'approved',
        isOwn: user && comment?.user_id === user?.id
      })) || [];

      setComments(formattedComments);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: `/blog/${blogSlug}` } });
      return;
    }

    if (!newComment?.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);

      // Sanitize input
      const sanitizedComment = sanitizeInput(newComment);

      // Check for vulgar content
      const moderation = moderateContent(sanitizedComment);

      // Determine status based on content moderation
      const commentStatus = moderation.isClean ? 'approved' : 'flagged';

      const { error: insertError } = await supabase
        ?.from('blog_comments')
        ?.insert([{
          blog_id: blogId,
          user_id: user?.id,
          author_name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'Anonymous',
          author_email: user?.email,
          author_role: userProfile?.role || 'Reader',
          comment_text: sanitizedComment,
          status: commentStatus
        }]);

      if (insertError) throw insertError;

      if (moderation.isClean) {
        toast.success('Comment posted successfully!');
        setNewComment('');
        // Refresh comments to show the new one
        fetchComments();
      } else {
        toast('Your comment contains unacceptable content and has been flagged for review. It will not be publicly visible until reviewed by a moderator.', { icon: '⚠️', duration: 5000 });
        setNewComment('');
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
      toast.error('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditedText(comment.text);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditedText('');
  };

  const handleSaveEdit = async (commentId) => {
    if (!editedText?.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      const sanitizedComment = sanitizeInput(editedText);
      const moderation = moderateContent(sanitizedComment);
      const commentStatus = moderation.isClean ? 'approved' : 'flagged';

      const { error: updateError } = await supabase
        ?.from('blog_comments')
        ?.update({
          comment_text: sanitizedComment,
          status: commentStatus
        })
        ?.eq('id', commentId)
        ?.eq('user_id', user?.id);

      if (updateError) throw updateError;

      if (moderation.isClean) {
        toast.success('Comment updated successfully!');
      } else {
        toast('Your edited comment contains unacceptable content and has been flagged for review.', { icon: '⚠️', duration: 5000 });
      }

      setEditingCommentId(null);
      setEditedText('');
      fetchComments();
    } catch (err) {
      console.error('Error updating comment:', err);
      toast.error('Failed to update comment. Please try again.');
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        ?.from('blog_comments')
        ?.delete()
        ?.eq('id', commentId)
        ?.eq('user_id', user?.id);

      if (deleteError) throw deleteError;

      toast.success('Comment deleted successfully!');
      fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
      toast.error('Failed to delete comment. Please try again.');
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-lg p-6 md:p-10 border border-border mt-8">
      <h3 className="text-2xl font-bold text-foreground mb-6">Comments ({comments?.length})</h3>
      
      {/* Comment Form */}
      {!user ? (
        <div className="mb-8 pb-8 border-b border-border bg-muted/30 rounded-lg p-6 text-center">
          <Icon name="Lock" size={32} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-foreground mb-4">You must be logged in to comment</p>
          <Button
            variant="default"
            iconName="LogIn"
            iconPosition="left"
            onClick={() => navigate('/login', { state: { from: `/blog/${blogSlug}` } })}
          >
            Log In to Comment
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mb-8 pb-8 border-b border-border">
          <div className="flex gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold">
                {userProfile?.full_name?.split(' ')?.map((n) => n?.[0])?.join('') || user?.email?.[0]?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground mb-1">
                {userProfile?.full_name || user?.email?.split('@')?.[0] || 'Anonymous'}
              </p>
              <p className="text-xs text-muted-foreground">{userProfile?.role || 'Reader'}</p>
            </div>
          </div>
          <div className="space-y-4">
            <textarea
              placeholder="Share your thoughts..."
              value={newComment}
              onChange={(e) => setNewComment(e?.target?.value)}
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              required
            />
            <div className="flex items-center justify-between">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Icon name="Loader" size={16} className="animate-spin" />
                    <span>Posting...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={16} />
                    <span>Post Comment</span>
                  </>
                )}
              </button>
              <p className="text-xs text-muted-foreground">
                Comments are auto-published if appropriate
              </p>
            </div>
          </div>
        </form>
      )}

      {/* Comments List */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}
      {!loading && comments?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="MessageSquare" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No comments yet. Be the first to comment!</p>
        </div>
      )}
      {!loading && comments?.length > 0 && (
        <div className="space-y-6">
          {comments?.map((comment) => (
            <div key={comment?.id} className={`flex gap-4 ${comment?.isOwn ? 'bg-primary/5 -mx-4 px-4 py-4 rounded-lg border border-primary/20' : ''}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold">
                  {comment?.author?.split(' ')?.map((n) => n?.[0])?.join('')}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-foreground">{comment?.author}</p>
                  {comment?.isOwn && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">You</span>
                  )}
                  {comment?.verified && (
                    <Icon name="CheckCircle" size={16} className="text-success" />
                  )}
                  {comment?.status === 'flagged' && (
                    <span className="text-xs bg-error/20 text-error px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      Flagged
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  {comment?.role} • {new Date(comment?.date)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                
                {editingCommentId === comment?.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e?.target?.value)}
                      rows={3}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(comment?.id)}
                        className="bg-primary text-primary-foreground px-4 py-1.5 rounded-lg hover:bg-primary/90 transition-smooth text-sm flex items-center gap-1"
                      >
                        <Icon name="Check" size={14} />
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="bg-muted text-foreground px-4 py-1.5 rounded-lg hover:bg-muted/80 transition-smooth text-sm flex items-center gap-1"
                      >
                        <Icon name="X" size={14} />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-foreground">{comment?.text}</p>
                    {comment?.isOwn && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleEdit(comment)}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-smooth"
                        >
                          <Icon name="Edit" size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(comment?.id)}
                          className="text-xs text-error hover:text-error/80 flex items-center gap-1 transition-smooth"
                        >
                          <Icon name="Trash" size={14} />
                          Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;